import React, { useEffect, useState } from 'react';
import { SxStyle } from '../../../../../types';
import { useStringsContext } from '../../../../../contexts/strings';
import { SquadPlayer } from '../../types';
import { HealthStatus } from '../../api-types';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import TableBody from '@mui/material/TableBody';
import PlayerTabContent from './player-tab-content';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';

interface FilteredHealthStatus extends Pick<HealthStatus, 'description' | 'fromDate' | 'toDate'> {
  // Additional fake properties
  status: string;
}

const filteredHealthKeys: (keyof FilteredHealthStatus)[] = [
  'status',
  'description',
  'fromDate',
  'toDate',
];

interface HealthRowsType {
  key: keyof FilteredHealthStatus;
  value: FilteredHealthStatus[keyof FilteredHealthStatus];
}

const styles: SxStyle = {
  tableRow: (theme) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }),
};

interface Props {
  player: SquadPlayer;
  onEditPlayer: () => void;
  season: number;
  isReadOnly?: boolean;
}

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;
const CURRENT_YEAR = parseInt(REACT_APP_DEFAULT_LEAGUE_SEASON as string, 10);

const PlayerTabHealth = ({ player, onEditPlayer, season, isReadOnly }: Props) => {
  const { strings } = useStringsContext();
  const [rows, setRows] = useState<HealthRowsType[] | null>(null);

  useEffect(() => {
    if (player.healthStatus) {
      const { healthStatus } = player;
      const healthRows = filteredHealthKeys.map((key) => {
        let value = null;
        if (key === 'status') {
          value = healthStatus?.injured
            ? strings.squadPlayer.injured
            : strings.squadPlayer.notInjured;
        } else {
          value = healthStatus[key];
        }
        return { key: key, value: value };
      });
      setRows(healthRows);
    }
  }, [player, strings]);

  if (!rows) {
    return <></>;
  }

  return (
    <PlayerTabContent
      title={strings.squadPlayerTabs.health}
      buttonElement={
        <Button
          variant="contained"
          onClick={onEditPlayer}
          startIcon={<EditIcon />}
          disabled={season !== CURRENT_YEAR || isReadOnly}
        >
          {strings.squad.edit}
        </Button>
      }
    >
      <TableBody>
        {rows?.map((row) => (
          <TableRow key={row.key} sx={styles.tableRow}>
            <TableCell width={300}>
              <Typography variant="subtitle2">{strings.squadPlayerHealth[row.key]}</Typography>
            </TableCell>
            <TableCell>
              {row.value || (
                <Typography variant="italic">{strings.squad.missingInformation}</Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </PlayerTabContent>
  );
};

export default PlayerTabHealth;
